import type { ActionTree } from 'vuex'
import dayjs from 'dayjs'
import type { CustomApiError } from '@audience-republic/audience-manager-shared/api/types'
import {
  pruneScratchEventForServer,
  getSalesChartContexts, getEventNormalCampaignsCount,
  getTimescaleData, getTimescaleRawDataFromSales, defaultSalesChartContexts,
  defaultSalesTimescaleContexts, setInitialSalesChartData, optimizeData, runPromises,
} from './utils'
import type { Event, EventState, ScratchEvent, EventAnnotationPostResponse, CreatedEventAnnotation, EventGroup, EventGroupSearchParams, UpcomingEventsSearchParams, BatchArchiveResult, BatchUnarchiveResult, BatchArchiveEventsResponse, BatchArchiveEventGroupsResponse, BatchUnarchiveEventsResponse, BatchUnarchiveEventGroupsResponse } from '@/types/resources/event'
import type { RootState } from '@/store/modules/types'
import { sanitizeHtmlChild } from '@/utils/html-element/'
import { fixedEncodeURIComponent } from '@/utils/helpers'
import { getMessageNameForPowerup } from '@/utils/powerups'
import { getContext } from '@/utils/general'
import { requestApi } from '@/utils/requestApi'

export function getAnnotationFromAPIResponse({ oid, eventOid, promoterOid, userDefined, type, body, timestamp }: EventAnnotationPostResponse): CreatedEventAnnotation {
  return {
    oid,
    eventOid,
    promoterOid,
    userDefined,
    type,
    body,
    timestamp,
  }
}

const defaultSelectString = 'name,description,campaigns,meta,capacity,location,startDate,endDate,timeZone,tourOid,presentation,resources,ticket-stats,paymentInfo,provider'

const BATCH_SIZE = 100
const MAX_CONCURRENT_BATCHES = 3

function chunk<T>(arr: T[], size: number): T[][] {
  const result: T[][] = []
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size))
  }
  return result
}

async function promisePool<T>(
  tasks: (() => Promise<T>)[],
  concurrency: number,
): Promise<T[]> {
  const results: T[] = new Array(tasks.length)
  const executing = new Set<Promise<void>>()

  for (let index = 0; index < tasks.length; index++) {
    const task = tasks[index]
    const p = task().then((result) => {
      results[index] = result
    }).finally(() => {
      executing.delete(p)
    })
    executing.add(p)

    if (executing.size >= concurrency) {
      await Promise.race(executing)
    }
  }

  await Promise.all(executing)
  return results
}

export const eventActions: ActionTree<EventState, RootState> = {
  async UPDATE_SALES_CHART_TIMEZONE({ dispatch, commit }, { timezone, timescaleActive }): Promise<void> {
    commit('SET_EVENT_SALES_CHART_TIMEZONE', timezone)
    dispatch('UPDATE_CURRENT_EVENT_CHART_DATA_TIMEZONE', timezone)
    dispatch('UPDATE_TIMESCALE_DATA_TIMEZONE', timezone)
    dispatch('UPDATE_OVERLAY_CHART_DATA_TIMEZONE', timezone)
  },

  async UPDATE_OVERLAY_CHART_DATA_TIMEZONE({ state, dispatch, commit }, timezone): Promise<void> {
    const overlayData = state.rawSalesOverlayData!
    if (!overlayData) return

    const compareData = defaultSalesChartContexts()

    let i = overlayData!.length
    while (i--) {
      const chartData = getSalesChartContexts(overlayData[i].data, timezone)

      compareData.sales.accumulative = [...compareData.sales.accumulative, { oid: overlayData[i].data.oid, name: overlayData[i].name, data: chartData.totalSales.sales }]
      compareData.sales.incremental = [...compareData.sales.incremental, { oid: overlayData[i].data.oid, name: overlayData[i].name, data: chartData.incrementalSales.sales }]
      compareData.quantity.accumulative = [...compareData.quantity.accumulative, { oid: overlayData[i].data.oid, name: overlayData[i].name, data: chartData.totalSales.quantity }]
      compareData.quantity.incremental = [...compareData.quantity.incremental, { oid: overlayData[i].data.oid, name: overlayData[i].name, data: chartData.incrementalSales.quantity }]
    }

    dispatch('ADD_TICKET_SALES_SERIES', [compareData])
  },

  async UPDATE_TIMESCALE_DATA_TIMEZONE({ state, commit }, timezone): Promise<void> {
    if (!state.eventTicketSales) {
      return
    }

    const updatedTimescaleData = await getTimescaleData(getTimescaleRawDataFromSales(state.eventTicketSales!), timezone)

    commit('UPDATE_TIMESCALE_DATA_TIMEZONE', updatedTimescaleData)
  },

  async UPDATE_CURRENT_EVENT_CHART_DATA_TIMEZONE({ state, dispatch, commit }, timezone): Promise<void> {
    // adjust and update default sales chart data
    const oid = state.currentEvent!.oid
    const updatedTimezoneData = getSalesChartContexts(state.rawSalesChartData!, timezone)

    const updatedData = {
      sales: {
        accumulative: [{
          oid: oid,
          name: 'Sales',
          data: updatedTimezoneData.totalSales.sales,
        }],
        incremental: [{
          oid: oid,
          name: 'Sales',
          data: updatedTimezoneData.incrementalSales.sales,
        }],
      },
      quantity: {
        accumulative: [{
          oid: oid,
          name: 'Sales',
          data: updatedTimezoneData.totalSales.quantity,
        }],
        incremental: [{
          oid: oid,
          name: 'Sales',
          data: updatedTimezoneData.incrementalSales.quantity,
        }],
      },
    }

    commit('UPDATE_CURRENT_EVENT_CHART_DATA_TIMEZONE', updatedData)
  },

  async UPDATE_ANNOTATION({ state, commit, rootState }, annotation): Promise<void> {
    const { promoterOid } = rootState.authModule.account!
    const { oid: eventOid } = state.currentEvent!

    const payload = {
      oid: annotation.oid,
      body: annotation.body,
      title: annotation.title,
    }

    const { $api } = getContext()

    try {
      await $api.eventAnnotations.update(promoterOid, eventOid, payload)

      commit('UPDATE_ANNOTATION', payload)
    }
    catch (error) {
      throw error
    }
  },

  async DELETE_ANNOTATION({ state, commit, rootState }, annotationOid): Promise<void> {
    const { promoterOid } = rootState.authModule.account!
    const { oid: eventOid } = state.currentEvent!
    const { $api } = getContext()

    try {
      await $api.eventAnnotations.delete(promoterOid, eventOid, annotationOid)
      commit('DELETE_ANNOTATION', annotationOid)
    }
    catch (error) {
      throw error
    }
  },

  CLEAR_ANNOTATIONS({ commit }) {
    commit('CLEAR_ANNOTATIONS')
  },

  async FETCH_ANNOTATIONS({ state, commit, rootState }): Promise<void> {
    console.log('Store: FETCH_ANNOTATIONS called')

    if (!state.currentEvent) return

    const { promoterOid } = rootState.authModule.account!
    const { oid } = state.currentEvent

    console.log('Store: Fetching for promoter:', promoterOid, 'event:', oid)
    const { $api } = getContext()

    try {
      const data = await $api.eventAnnotations.get(promoterOid, oid)
      console.log('Store: Fetched annotation data:', data)

      const annotations = []
      let i = data.length
      while (i--) {
        annotations.push(getAnnotationFromAPIResponse(data[i]))
      }

      console.log('Store: Processed annotations:', annotations)
      commit('SET_INITIAL_ANNOTATIONS', annotations)
      console.log('Store: Set in store, current annotations:', state.eventAnnotations)
    }
    catch (error) {
      console.error('Store: FETCH_ANNOTATIONS error:', error)
      throw error
    }
  },

  async CREATE_ANNOTATION({ state, commit, rootState }, annotation): Promise<CreatedEventAnnotation> {
    console.log('Store: CREATE_ANNOTATION called with:', annotation)

    const { promoterOid } = rootState.authModule.account!
    const { oid } = state.currentEvent!

    const annotationPayload = {
      eventOid: oid,
      promoterOid: promoterOid,
      userDefined: true,
      type: 'custom',
      body: annotation.body,
      timestamp: annotation.timestamp,
    }

    console.log('Store: API payload:', annotationPayload)
    const { $api } = getContext()

    try {
      const data = await $api.eventAnnotations.create(promoterOid, oid, annotationPayload)
      console.log('Store: API response:', data)

      const anno = getAnnotationFromAPIResponse(data)
      console.log('Store: Processed annotation:', anno)

      commit('ADD_ANNOTATION_TO_EVENT', anno)
      console.log('Store: Added to store, current annotations:', state.eventAnnotations)

      return anno
    }
    catch (error) {
      console.error('Store: CREATE_ANNOTATION error:', error)
      throw error
    }
  },

  async FETCH_LINE_ANNOTATIONS({ state, commit, rootState }): Promise<void> {
    const { promoterOid } = rootState.authModule.account!
    const { oid } = state.currentEvent!
    const { $axios } = getContext()

    try {
      const { data } = await $axios.get(`/promoter/${promoterOid}/task/${oid}/graph-annotations`)

      commit('SET_LINE_ANNOTATIONS', data)
    }
    catch (error) {
      throw error
    }
  },

  async FETCH_COMPARE_EVENT_TICKET_SALES_DATA({ rootState },
    {
      oid = 0,
      name = '',
      eventOid = 0,
      searchString = '',
      top = 50,
      orderBy = { key: 'sysMtime', order: 'desc' },
      filter = null,
      selectKeys = null,
      reload = false,
    }) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()

    try {
      const params: any = {}

      if (filter && filter.conditions.length > 0) {
        params.$audienceFilter = filter
      }

      const data = await $api.event.getTicketSales(promoterOid, oid, params)

      return {
        name: name,
        data: data,
      }
    }
    catch (error) {
      console.error(error)

      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch event ticket sales',
      })

      return null
    }
  },

  SET_SALES_CHART_DATA_LOADING({ commit }, bool) {
    commit('SALES_CHART_DATA_IS_LOADING', bool)
  },

  async SET_OVERLAY_DATA({ state, dispatch, commit }, payload) {
    const data = payload.data
    const overlayObjects: any = {}
    const promiseObject: any = {}

    let i = data.length
    while (i--) {
      // add oid and name to return object while sending data to be optimized
      overlayObjects[data[i].name] = { oid: data[i].oid, name: data[i].name }
      promiseObject[data[i].name] = optimizeData(data[i].data)
    }

    const chartData = await runPromises(promiseObject)
    Object.entries(chartData).forEach(([key, val]) => {
      // add optimized data to return object, where event oid and name already exist
      overlayObjects[key].data = Object.freeze(val)
    })

    const optimizedData = Object.values(overlayObjects)

    commit('SET_RAW_SALES_OVERLAY_DATA', optimizedData)

    const compareData = defaultSalesChartContexts()
    const compareTimescaleArray = defaultSalesTimescaleContexts()
    const currentSalesChartTimezone = state.salesChartTimezone!
    const { $arWorker } = getContext()

    const overlaySalesQuantityWorker = $arWorker.createOverlaySalesQuantityWorker()
    const overlayTimescaleDataWorker = $arWorker.createOverlayTimescaleDataWorker()

    // listen for response and process data as normal
    overlaySalesQuantityWorker.addEventListener('message', (event: any) => {
      dispatch('ADD_TICKET_SALES_SERIES', [event.data])
    })

    // listen for response and process data as normal
    overlayTimescaleDataWorker.addEventListener('message', (event: any) => {
      commit('ADD_OVERLAY_TIMESCALE_DATA', event.data)
    })

    // here we call out worker functions, passing in the arguments needed to run
    overlaySalesQuantityWorker.postMessage({ data: optimizedData, timezone: currentSalesChartTimezone, contexts: compareData })
    overlayTimescaleDataWorker.postMessage({ data: data, timezone: currentSalesChartTimezone, contexts: compareTimescaleArray })
  },

  CLEAR_SALES_DATA({ commit }) {
    commit('CLEAR_SALES_DATA')
  },

  CLEAR_COMPARE_TIMESCALE_DATA({ commit }) {
    commit('CLEAR_COMPARE_TIMESCALE_DATA')
  },

  async SET_INITIAL_TIMESCALE_DATA({ state, commit }, data) {
    if (!data) {
      return
    }

    const currentSalesChartTimezone = state.salesChartTimezone!

    const initialTimescaleData = await getTimescaleData(getTimescaleRawDataFromSales(data), currentSalesChartTimezone)

    commit('SET_INITIAL_TIMESCALE_DATA', initialTimescaleData)
  },

  SET_DRAG_LISTENER_STATE({ commit }, payload) {
    commit('SET_DRAG_LISTENER_STATE', payload)
  },

  async SET_INITIAL_TICKET_SALES_CHART_DATA({ state, commit }, data) {
    const chartData = await optimizeData(data)
    const currentEventTimeZone = state.currentEvent!.timeZone
    const oid = state.currentEvent!.oid
    const { $arWorker } = getContext()
    console.log('$arWorker', $arWorker)

    commit('SET_RAW_SALES_CHART_DATA', data)
    const worker = $arWorker.createInitialEventSalesDataWorker()

    worker.addEventListener('message', (event: any) => {
      const initialData = setInitialSalesChartData(oid, event.data)
      commit('SET_INITIAL_TICKET_SALES_CHART_DATA', initialData)
    })

    worker.postMessage(JSON.parse(JSON.stringify({ chartData, currentEventTimeZone })))
  },

  ADD_TICKET_SALES_SERIES({ commit }, payload) {
    let i = payload.length
    while (i--) {
      commit('ADD_TICKET_SALES_SERIES', payload[i])
    }
  },

  async FETCH_ALL_EVENT_PROVIDERS({ rootState }) {
    if (!rootState.authModule?.account?.promoterOid) {
      throw new Error('Auth not available')
    }
    const { promoterOid } = rootState.authModule.account

    const { $axios } = getContext()

    try {
      // Fetch distinct providers from all events
      const { data } = await $axios.get(`/promoter/${promoterOid}/event`, {
        params: {
          $select: 'provider',
          $filter: 'userDefined AND provider IS NOT NULL',
          $top: 1000, // Get a large number to ensure we capture all providers
        },
      })

      // Extract unique providers from the response
      const providers = new Set<string>()
      data.forEach((event: { provider: string }) => {
        if (event.provider) {
          providers.add(event.provider)
        }
      })

      return Array.from(providers).sort()
    }
    catch (error) {
      console.error('Failed to fetch all event providers:', error)
      throw error
    }
  },

  async FETCH_EVENT_COUNT(
    { state, commit, rootState },
    {
      searchString = '',
      filter = null,
      dateRange = null,
      provider = 'all',
      promoterOid = null,
    },
  ) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid: currentPromoterOid } = rootState.authModule.account
    commit('SET_TOTAL_EVENTS_COUNT', 0)
    const filters = ['userDefined']
    if (searchString) {
      filters.push(`name ILIKE "%${searchString}%"`)
    }
    if (filter === 'eventbrite') {
      filters.push('provider=eventbrite')
    }
    filters.push(`promoterOid=${promoterOid || currentPromoterOid}`)
    if (provider && provider !== 'all') {
      filters.push(`provider=${provider}`)
    }
    const filterString = filters.join(' AND ') || null

    let filterDate
    if (dateRange) {
      switch (dateRange.condition) {
        case 'is_equal_to':

          const startOfDay = dayjs(dateRange.values[0]).utc(true).subtract(12, 'hours').format('YYYY-MM-DDTHH:mm:ss')
          const endOfDay = dayjs(dateRange.values[0]).utc(true).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
          filters.push(`startDate > '${startOfDay}' AND startDate < '${endOfDay}'`)

          break
        case 'is_after':
          filterDate = dayjs(dateRange.values[0]).utc(true).subtract(12, 'hours').format('YYYY-MM-DD')
          filters.push(`startDate > '${filterDate}'`)

          break
        case 'is_before':
          filterDate = dayjs(dateRange.values[0]).utc(true).format('YYYY-MM-DD')
          filters.push(`startDate < '${filterDate}'`)

          break
        case 'is_between':
          const filterStart = dayjs(dateRange.values[0]).utc(true).format('YYYY-MM-DD')
          const filterEnd = dayjs(dateRange.values[1]).utc(true).format('YYYY-MM-DD')
          filters.push(`startDate > '${filterStart}'`)
          filters.push(`startDate < '${filterEnd}'`)

          break
        default:
          console.log('An unrecognized date range condition was received: ', dateRange)
      }
    }

    const { $api } = getContext()

    try {
      const data = await $api.event.fetchCount(promoterOid, { filter: filterString })
      const eventsCount: number = data ? data.count : 0
      commit('SET_TOTAL_EVENTS_COUNT', eventsCount)
    }
    catch (e) {
      console.error('Failed to fetch total events count!')
      console.error(e)
      commit('SET_TOTAL_EVENTS_COUNT', 0)
    }
  },

  async FETCH_MORE_EVENTS(
    { state, commit, rootState },
    {
      orderBy = null,
      top = 12,
      searchString = '',
      filter = null,
      reload = false,
      fetchCount = false,
      dateRange = null,
      provider = 'all',
      promoterOid = null,
      selectString = null, // Optional minimal select for dropdowns
      skip = undefined,
    },
  ) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid: currentPromoterOid } = rootState.authModule.account

    // An explicit `skip` (page-based pagination) must be captured before RESET_EVENTS
    // clears state.events below - callers relying on the implicit array-length skip
    // (infinite scroll/reload-to-page-1) still get it computed after the reset, same
    // as before.
    const explicitSkip = typeof skip === 'number' ? skip : undefined

    if (reload) {
      commit('RESET_EVENTS')
    }

    const filters = ['userDefined']
    if (searchString) {
      filters.push(`(name ILIKE "%${searchString}%" OR location ILIKE "%${searchString}%")`)
    }
    if (filter === 'eventbrite') {
      filters.push('provider=eventbrite')
    }
    filters.push(`promoterOid=${promoterOid || currentPromoterOid}`)
    if (provider && provider !== 'all') {
      filters.push(`provider=${provider}`)
    }

    let filterDate
    if (dateRange) {
      switch (dateRange.condition) {
        case 'is_equal_to':

          const startOfDay = dayjs(dateRange.values[0]).utc(true).subtract(12, 'hours').format('YYYY-MM-DDTHH:mm:ss')
          const endOfDay = dayjs(dateRange.values[0]).utc(true).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
          filters.push(`startDate > '${startOfDay}' AND startDate < '${endOfDay}'`)

          break
        case 'is_after':
          filterDate = dayjs(dateRange.values[0]).utc(true).subtract(12, 'hours').format('YYYY-MM-DD')
          filters.push(`startDate > '${filterDate}'`)

          break
        case 'is_before':
          filterDate = dayjs(dateRange.values[0]).utc(true).format('YYYY-MM-DD')
          filters.push(`startDate < '${filterDate}'`)

          break
        case 'is_between':
          const filterStart = dayjs(dateRange.values[0]).utc(true).format('YYYY-MM-DD')
          const filterEnd = dayjs(dateRange.values[1]).utc(true).format('YYYY-MM-DD')
          filters.push(`startDate > '${filterStart}'`)
          filters.push(`startDate < '${filterEnd}'`)

          break
        default:
          console.log('An unrecognized date range condition was received: ', dateRange)
      }
    }

    const filterString = filters.join(' AND ') || null

    let orderby
    switch (orderBy) {
      case 'datecreated': orderby = 'sysCtime desc'; break
      case 'eventstartdateasc': orderby = 'startDate asc'; break
      case 'eventstartdatedesc': orderby = 'startDate desc'; break
      case 'alphabetically': orderby = 'name asc'; break
      default:
        orderby = orderBy || `oid desc`
    }

    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_EVENTS', true)
      const { data } = await $axios.get(`/promoter/${promoterOid || currentPromoterOid}/event`, {
        params: {
          $select: selectString || 'name,description,campaigns,meta,capacity,location,startDate,endDate,timeZone,tourOid,presentation,resources,ticket-stats,paymentInfo,additionalInfo,provider',
          $top: top,
          $skip: explicitSkip ?? state.events.length,
          $count: fetchCount,
          $filter: filterString,
          $orderby: orderby,
        },
      })
      const events: Event[] = fetchCount ? data.rows : data
      const eventsCount: number = fetchCount ? data.count : null

      if (fetchCount && eventsCount !== null) {
        commit('SET_TOTAL_EVENTS_COUNT', eventsCount)
      }

      if (reload) {
        commit('SET_EVENTS', events)
      }
      else {
        commit('CONCAT_EVENTS', events)
      }
      if (events.length === 0) {
        commit('SET_HAS_NO_MORE_EVENTS', true)
      }
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch events',
      })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_EVENTS', false)
    }
  },
  async FETCH_CURRENT_EVENT({ rootState, commit }, eventOid: number) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_EVENT', true)
      const event = await $api.event.get(promoterOid, eventOid)

      console.log('FETCH_CURRENT_EVENT', event)
      commit('SET_CURRENT_EVENT', event)
      commit('SET_EVENT_SALES_CHART_TIMEZONE', event!.timeZone)

      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: `Failed to fetch event`,
      })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_EVENT', false)
    }
  },
  async CREATE_EVENT({ commit, rootState }, scratchEvent: ScratchEvent) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account

    const eventData = pruneScratchEventForServer(scratchEvent)
    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_CREATING_EVENT', true)
      const { data } = await $axios.post(`/promoter/${promoterOid}/new-event`, eventData)

      $arNotification.push({
        type: 'success',
        message: 'Event created successfully',
      })
      return data
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: error.response.status === 422 ? 'There was an error creating your event, please check the submitted fields.' : 'Failed to create event',
      })
      return null
    }
    finally {
      commit('SET_IS_CREATING_EVENT', false)
    }
  },
  async UPDATE_EVENT({ rootState, commit }, scratchEvent: ScratchEvent) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    if (!scratchEvent.oid) { return null }

    const eventData = pruneScratchEventForServer(scratchEvent)
    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_UPDATING_EVENT', true)
      await $api.event.patch(promoterOid, scratchEvent.oid, eventData)

      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to update event',
      })
      return false
    }
    finally {
      commit('SET_IS_UPDATING_EVENT', false)
    }
  },
  async CONNECT_TO_ZOOM({ rootState }, { eventOid, integrationOid, accountId }) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $api } = getContext()

    try {
      await $api.event.connectToZoom(promoterOid, eventOid, integrationOid, accountId)
    }
    catch (error) {
      console.error(error)
    }
  },
  async CLONE_EVENT({ rootState }, event: Event) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()

    try {
      const newEvent: Event = await $api.event.clone(promoterOid, event.oid)

      $arNotification.push({
        type: 'success',
        message: `Event "${event.name}" successfully duplicated`,
      })
      return newEvent.oid
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: `Failed to duplicate event "${event.name}"`,
      })
      return null
    }
  },
  async DELETE_EVENT({ rootState, dispatch }, eventOrPayload: Event | { event: Event, skipConfirmation?: boolean }) {
    console.log('DELETE_EVENT')
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account

    // Handle both old signature (just Event) and new signature ({ event, skipConfirmation })
    const event = 'oid' in eventOrPayload ? eventOrPayload : eventOrPayload.event
    const skipConfirmation = 'oid' in eventOrPayload ? false : (eventOrPayload.skipConfirmation ?? false)

    const eventNormalCampaignsCount = getEventNormalCampaignsCount(event)
    const { $axios, $arNotification } = getContext()

    // Skip confirmation if requested (when called from DeleteEventsModal)
    let agreed = skipConfirmation

    if (!skipConfirmation) {
      // We have to promote to ask users if they want to delete this event or not
      if (eventNormalCampaignsCount === 0) {
        agreed = await dispatch('SHOW_CONFIRM', {
          messageHtml: `Are you sure you want to delete the event<br /><b>${sanitizeHtmlChild(event.name)}</b>?`,
          hideConfirmButton: false,
        }, { root: true })
      }
      else {
        let confirmationCopy
        if (eventNormalCampaignsCount === 1) {
          confirmationCopy = `There is <b>${eventNormalCampaignsCount} campaign</b> associated with this event. You must delete the campaign before you can delete this event.`
        }
        else {
          confirmationCopy = `There are <b>${eventNormalCampaignsCount} campaigns</b> associated with this event. You must delete these campaigns before you can delete this event.`
        }
        agreed = await dispatch('SHOW_CONFIRM', {
          messageHtml: confirmationCopy,
          hideConfirmButton: true,
        }, { root: true })
      }
    }

    if (!agreed) { return null }

    try {
      await $axios.delete(`/promoter/${promoterOid}/event/${event.oid}`)

      $arNotification.push({
        type: 'success',
        message: `Event "${event.name}" deleted`,
      })
      return event.oid
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: `Failed to delete event "${event.name}"`,
      })
      return null
    }
  },
  async MERGE_EVENTS(
    { rootState, commit },
    { sourceEvent, targetEvent },
  ) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account

    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_MERGING_EVENTS', true)
      const { status } = await $axios.post(`/promoter/${promoterOid}/event-merge`, {
        sourceEventOid: sourceEvent.oid,
        targetEventOid: targetEvent.oid,
      })
      let message = ''
      if (status === 202) {
        message = 'has started merging process. This may take some time.'
      }
      else {
        message = 'has now been merged'
      }

      $arNotification.push({
        type: 'success',
        message: `${sourceEvent.name} ${message}`,
      })
      return true
    }
    catch (error: any) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: error.message || error,
      })
      return false
    }
    finally {
      commit('SET_IS_MERGING_EVENTS', false)
    }
  },
  async FETCH_EVENT_TICKET_STATS({ rootState, commit },
    {
      eventOid = 0,
      searchString = '',
      top = 50,
      orderBy = { key: 'sysMtime', order: 'desc' },
      filter = null,
      selectKeys = null,
      reload = false,
    }) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account

    // Validate eventOid is a valid number
    if (!eventOid || isNaN(eventOid)) {
      console.warn('FETCH_EVENT_TICKET_STATS: Invalid eventOid', eventOid)
      return null
    }

    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_EVENT_TICKET_STATS', true)

      const params: any = {}

      if (filter && filter.conditions.length > 0) {
        params.$audienceFilter = JSON.stringify(filter)
      }

      console.log('getTicketStats | eventOid', eventOid)

      const data = await $api.event.getTicketStats(promoterOid, eventOid, params)

      commit('SET_EVENT_TICKET_STATS', data)
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch event ticket stats',
      })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_EVENT_TICKET_STATS', false)
    }
  },
  async FETCH_EVENT_TICKET_SALES({ state, commit, rootState }, {
    eventOid = null,
    filter = null,
  }) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account

    // Validate eventOid is a valid number
    if (!eventOid || isNaN(eventOid)) {
      console.warn('FETCH_EVENT_TICKET_SALES: Invalid eventOid', eventOid)
      return null
    }

    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_EVENT_TICKET_SALES', true)
      const params: any = {}

      if (filter && filter.conditions.length > 0) {
        params.$audienceFilter = JSON.stringify(filter)
      }

      const data = await $api.event.getTicketSales(promoterOid, eventOid, params)

      commit('SET_EVENT_TICKET_SALES', data)
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch event ticket sales',
      })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_EVENT_TICKET_SALES', false)
    }
  },

  async DELETE_POS_ORDERS({ state, commit, rootState }, event: Event) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()

    try {
      await $api.event.deleteEventPosOrders(promoterOid, event.oid)
      $arNotification.push({
        type: 'success',
        message: 'POS orders cleared',
      })
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to delete POS orders',
      })
      return false
    }
  },

  async DELETE_TICKET_SALES({ rootState }, event: Event) {
    if (!rootState.authModule.account) { return false }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()

    try {
      await $api.event.deleteEventTicketSales(promoterOid, event.oid)
      $arNotification.push({
        type: 'success',
        message: 'Ticket sales will be cleared shortly',
      })
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to clear ticket sales',
      })
      return false
    }
  },

  // Event List
  async FETCH_EVENTS_LIST(
    { state, rootState, commit },
    { orderBy = null, $top = null, selectString = defaultSelectString, provider = null }): Promise<void> {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    if (!promoterOid) return

    if (state.isFetchingEventsList || state.isLastEventListLoaded) return

    commit('SET_IS_FETCHING_EVENTS_LIST', true)

    const skip = state.eventsListLoaded || 0
    const top = $top || state.eventsListStepLoadCount

    let url = `/promoter/${promoterOid}/event?$select=${selectString}&$top=${top}&$skip=${skip}&$count=true`

    const filterCriteria = ['userDefined']

    if (state.eventsListSearch) {
      const searchBy = ['name', 'location']

      const filterBy = searchBy.map(s =>
        fixedEncodeURIComponent(`${s} ILIKE "%${state.eventsListSearch}%"`),
      )

      filterCriteria.push(`(${filterBy.join(' OR ')})`)
    }

    if (provider && provider !== 'all') {
      filterCriteria.push(`provider=${provider}`)
    }

    if (filterCriteria.length > 0) {
      url += `&$filter=${filterCriteria.join(' AND ')}`
    }

    switch (orderBy) {
      case 'datecreated': url += '&$orderby=sysCtime desc'; break
      case 'eventstartdateasc': url += '&$orderby=startDate asc'; break
      case 'eventstartdatedesc': url += '&$orderby=startDate desc'; break
      case 'alphabetically': url += '&$orderby=name asc'; break
      default: url += `&$orderby=oid desc`
    }

    const { $axios, $arNotification } = getContext()

    try {
      const { data: { rows, count } } = await $axios.get(url)

      commit('CONCAT_EVENTS_LIST', rows)
      if (count) commit('SET_EVENTS_LIST_TOTAL_COUNT', count)

      if (state.eventsListLoaded + state.eventsListStepLoadCount >= state.eventsListTotalCount) {
        commit('SET_IS_LAST_EVENT_LIST_LOADED', true)
      }
      commit('SET_EVENTS_LIST_LOADED', state.eventsListLoaded + state.eventsListStepLoadCount)
    }
    catch (error) {
      $arNotification.push({ type: 'error', message: 'There was an error fetching the events' })
    }
    finally {
      commit('SET_IS_FETCHING_EVENTS_LIST', false)
    }
  },

  async IMPORT_ONESCHEMA_CSV({ rootState, dispatch, commit }, payload) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()

    try {
      const res = await $axios.post(`/promoter/${promoterOid}/oneschema-import`, payload)

      $arNotification.push({
        type: 'success',
        message: `Import has started. Your data will be added soon`,
      })
    }
    catch (error: any) {
      console.error(error)
      const serverReason = error.response && error.response.message ? error.response.message.details : error
      $arNotification.push({
        type: 'error',
        message: serverReason,
      })
    }
  },

  async CREATE_POWERUP_MESSAGE({ rootState, state, commit }, payload) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { eventOid, actionType, provider, scheduleMinutesOffset, timeZone, finalDate, days } = payload
    commit('SET_IS_CREATING_POWER_UP', true)
    const { $axios, $arNotification } = getContext()

    try {
      const params = {
        'event-oid': eventOid,
        'action-type': actionType,
        'provider': provider,
        'schedule-minutes-offset': scheduleMinutesOffset || 0,
      }

      const res = await $axios.post(`/promoter/${promoterOid}/event/${eventOid}/powerup`, params)

      const message = await $axios.get(`/promoter/${promoterOid}/task/${res.data.task.oid}`)

      let newMeta = {}

      if (provider === 'email') {
        newMeta = {
          ...message.data.meta,
          presentation: {
            timeZone: timeZone || null,
          },
          linkedEvents: [eventOid],
        }
      }
      if (provider === 'sms') {
        newMeta = {
          initiator: {
            eventOid: eventOid,
            campaignOid: null,
            powerup: true,
          },
          linkedEvents: [eventOid],
          bucketOid: null,
          dynamicTagHeaders: [
          ],
          facebookMessenger: {
            pageId: null,
            pageName: null,
          },
          messageBody: '',
          messageListOid: null,
          presentation: {
            timeZone: timeZone || null,
          },
          promoterSmsSenderOid: null,
          recipientFilter: null,
          tagMaxWidthMap: {
          },
          tagsResourceOid: null,
        }
      }

      const finalRes: { meta: any, scheduledAt?: string, customerName?: string } = {
        meta: newMeta,
      }

      if (state.currentEvent?.name && state.currentEvent?.timeZone) {
        finalRes['customerName'] = getMessageNameForPowerup({ actionType, days, eventName: state.currentEvent.name })
      }

      if (actionType === 'event-day' && finalDate) {
        finalRes['scheduledAt'] = finalDate
      }

      await $axios.patch(`/promoter/${promoterOid}/task/${res.data.task.oid}`, finalRes)

      $arNotification.push({
        type: 'success',
        message: `Power Up has been created`,
      })

      return res
    }
    catch (error: any) {
      console.error(error)
      const serverReason = error.response && error.response.message ? error.response.message.details : error
      $arNotification.push({
        type: 'error',
        message: serverReason,
      })
    }
    finally {
      commit('SET_IS_CREATING_POWER_UP', false)
    }
  },

  async FETCH_POWERUP_MESSAGES({ rootState, commit }, eventOid) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    commit('SET_IS_FETCHING_POWER_UPS', true)
    const { $axios, $arNotification } = getContext()

    try {
      const res = await $axios.get(`/promoter/${promoterOid}/event/${eventOid}/powerup?$filter=eventOid=${eventOid}`)

      if (res.data) commit('SET_EXISTING_POWER_UPS', res.data)
    }
    catch (error: any) {
      console.error(error)
      const serverReason = error.response && error.response.message ? error.response.message.details : error
      $arNotification.push({
        type: 'error',
        message: serverReason,
      })
    }
    finally {
      commit('SET_IS_FETCHING_POWER_UPS', false)
    }
  },

  async UPDATE_POWERUP_MESSAGE({ rootState, commit }, payload) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { eventOid, oid, actionType, provider, scheduleMinutesOffset } = payload
    const { $axios, $arNotification } = getContext()

    try {
      const params = {
        'provider': provider,
        'action-type': actionType,
        'schedule-minutes-offset': scheduleMinutesOffset || 0,
      }

      const res = await $axios.patch(`/promoter/${promoterOid}/event/${eventOid}/powerup/${oid}`, params)

      $arNotification.push({
        type: 'success',
        message: `Power Up has been updated`,
      })

      return res
    }
    catch (error: any) {
      console.error(error)
      const serverReason = error.response && error.response.message ? error.response.message.details : error
      $arNotification.push({
        type: 'error',
        message: serverReason,
      })
    }
  },
  async FETCH_EVENT_AUDIENCE_STATS({ rootState, commit },
    {
      eventOid = null,
    }) {
    if (!rootState.authModule.account) { return null }
    if (!eventOid) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_EVENT_AUDIENCE_STATS', true)

      const data = await $api.event.fetchEventAudienceStatistics(promoterOid, eventOid)
      commit('SET_EVENT_AUDIENCE_STATS', data)
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch event audience stats',
      })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_EVENT_AUDIENCE_STATS', false)
    }
  },

  // Refresh the event audience statistics
  async REFRESH_EVENT_AUDIENCE_STATS({ rootState, commit },
    {
      eventOid = null,
    }) {
    if (!rootState.authModule.account) { return null }
    if (!eventOid) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()

    try {
      const data = await $api.event.refreshEventAudienceStatistics(promoterOid, eventOid)
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to refresh event audience stats',
      })
      return false
    }
  },

  async DELETE_POWERUP_MESSAGE({ rootState, commit }, payload) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { eventOid, oid } = payload
    const { $axios, $arNotification } = getContext()

    try {
      const res = await $axios.delete(`/promoter/${promoterOid}/event/${eventOid}/powerup/${oid}`)

      $arNotification.push({
        type: 'success',
        message: `Power Up has been deleted`,
      })
    }
    catch (error: any) {
      console.error(error)
      const serverReason = error.response && error.response.message ? error.response.message.details : error
      $arNotification.push({
        type: 'error',
        message: serverReason,
      })
    }
  },

  async FETCH_ENGAGEMENT_STATS({ rootState, commit, state }, payload) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { eventOid } = payload
    if (!eventOid) { return null }
    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_ENGAGEMENT_INSIGHTS', true)
      const data = await $axios.get(`/promoter/${promoterOid}/event/${eventOid}/insight-engagement-stats`)
      commit('SET_ENGAGEMENT_INSIGHTS', data.data)
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch engagement stats',
      })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_ENGAGEMENT_INSIGHTS', false)
    }
  },

  async START_EVENT_SYNC({ rootState }, eventOid: number) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $axios } = getContext()

    const url = `/promoter/${promoterOid}/event/${eventOid}/sync`

    try {
      const res = await $axios.sg.post(url)

      if (res.status === 201) {
        return {
          accepted: true,
          event: res.data,
        }
      }
      else {
        throw new Error(res.data)
      }
    }
    catch (error: any) {
      const serverReason = error.message ? error.message : error
      return {
        accepted: false,
        message: serverReason,
      }
    }
  },

  async CHECK_EVENT_SYNC_STATUS({ rootState, commit }, eventOid: number) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $api } = getContext()

    try {
      // Fetch event without setting loading state - just to check sync status
      const event = await $api.event.get(promoterOid, eventOid)

      // Only update the current event object, don't trigger loading states
      commit('SET_CURRENT_EVENT', event)

      return true
    }
    catch (error: any) {
      console.error('Error checking sync status:', error)
      return false
    }
  },

  // Note: GROUP_EVENTS and UNGROUP_EVENTS actions have been replaced by:
  // - CREATE_EVENT_GROUP (create empty group)
  // - ADD_EVENTS_TO_GROUP (add events to group)
  // - DELETE_EVENT_GROUP (delete group)
  // These implement the proper two-step workflow for event grouping

  // Event Groups Actions

  async FETCH_EVENT_GROUPS({ commit, rootState, state }, params = {}) {
    const { reload = false, skip = 0 } = params

    if (reload) {
      commit('RESET_EVENT_GROUPS')
    }

    commit('SET_IS_FETCHING_EVENT_GROUPS', true)
    try {
      const { $api } = getContext()
      const promoterOid = rootState.authModule.account.promoterOid

      const response = await $api.event.searchEventGroups(promoterOid, params)

      if (skip === 0 || reload) {
        // Initial load or reload - replace all
        commit('SET_EVENT_GROUPS', {
          eventGroups: response.rows,
          totalCount: response.count,
        })
        const { top = 50 } = params
        commit('SET_HAS_NO_MORE_EVENT_GROUPS', response.rows.length < top)
      }
      else {
        // Scroll load - append
        commit('CONCAT_EVENT_GROUPS', response.rows)
        const { top = 25 } = params
        commit('SET_HAS_NO_MORE_EVENT_GROUPS', response.rows.length < top)
      }

      return response
    }
    catch (error) {
      console.error('Error fetching event groups:', error)
      throw error
    }
    finally {
      commit('SET_IS_FETCHING_EVENT_GROUPS', false)
    }
  },

  async GET_EVENT_GROUP({ commit, rootState }, eventGroupOid) {
    try {
      const { $api } = getContext()
      const promoterOid = rootState.authModule.account.promoterOid

      const eventGroup = await $api.event.getEventGroup(promoterOid, eventGroupOid)
      commit('SET_CURRENT_EVENT_GROUP', eventGroup)

      return eventGroup
    }
    catch (error) {
      console.error('Error fetching event group:', error)
      throw error
    }
  },

  async CREATE_EVENT_GROUP({ commit, rootState }, { name, artwork }) {
    console.log('CREATE_EVENT_GROUP', name, artwork)
    commit('SET_IS_CREATING_EVENT_GROUP', true)
    try {
      const { $api } = getContext()
      const promoterOid = rootState.authModule.account.promoterOid

      // Build payload - only include resourceOid if explicitly provided (not undefined)
      const payload: { name: string, resourceOid?: number | null } = { name }
      if (artwork !== undefined) {
        payload.resourceOid = artwork
      }

      const eventGroup = await $api.event.createEventGroup(promoterOid, payload)
      commit('ADD_EVENT_GROUP', eventGroup)

      return eventGroup
    }
    catch (error) {
      console.error('Error creating event group:', error)
      throw error
    }
    finally {
      commit('SET_IS_CREATING_EVENT_GROUP', false)
    }
  },

  async UPDATE_EVENT_GROUP({ commit, rootState }, { eventGroupOid, name, artwork }) {
    commit('SET_IS_UPDATING_EVENT_GROUP', true)
    try {
      const { $api } = getContext()
      const promoterOid = rootState.authModule.account.promoterOid

      // Build payload - only include resourceOid if explicitly provided (not undefined)
      // null is allowed and means "clear the artwork"
      const payload: { name: string, resourceOid?: number | null } = { name }
      if (artwork !== undefined) {
        payload.resourceOid = artwork
      }

      const updatedGroup = await $api.event.updateEventGroup(promoterOid, eventGroupOid, payload)
      commit('UPDATE_EVENT_GROUP', updatedGroup)

      return updatedGroup
    }
    catch (error) {
      console.error('Error updating event group:', error)
      throw error
    }
    finally {
      commit('SET_IS_UPDATING_EVENT_GROUP', false)
    }
  },

  async DELETE_EVENT_GROUP({ commit, rootState }, payload) {
    // Accept either just oid (number) or { oid, name } for backwards compatibility
    const eventGroupOid = typeof payload === 'number' ? payload : payload.oid
    const eventGroupName = typeof payload === 'object' ? payload.name : null

    console.log('DELETE_EVENT_GROUP', eventGroupOid, eventGroupName)
    try {
      const { $api, $arNotification } = getContext()
      const promoterOid = rootState.authModule.account.promoterOid

      await $api.event.deleteEventGroup(promoterOid, eventGroupOid)
      commit('DELETE_EVENT_GROUP', eventGroupOid)

      $arNotification.push({
        type: 'success',
        message: eventGroupName ? `Event group "${eventGroupName}" deleted` : 'Event group deleted',
      })

      return eventGroupOid
    }
    catch (error) {
      console.error('Error deleting event group:', error)
      const { $arNotification } = getContext()
      $arNotification.push({
        type: 'error',
        message: eventGroupName ? `Failed to delete event group "${eventGroupName}"` : 'Failed to delete event group',
      })
      throw error
    }
  },

  async ADD_EVENTS_TO_GROUP({ commit, rootState }, { eventGroupOid, eventOids }) {
    commit('SET_IS_ADDING_EVENTS_TO_GROUP', true)
    try {
      const { $api } = getContext()
      const promoterOid = rootState.authModule.account.promoterOid

      await $api.event.addEventsToGroup(promoterOid, eventGroupOid, { eventOids })

      // Optionally refresh the current event group to show updated events
      const updatedGroup = await $api.event.getEventGroup(promoterOid, eventGroupOid)
      commit('UPDATE_EVENT_GROUP', updatedGroup)

      return true
    }
    catch (error) {
      console.error('Error adding events to group:', error)
      throw error
    }
    finally {
      commit('SET_IS_ADDING_EVENTS_TO_GROUP', false)
    }
  },

  async REMOVE_EVENTS_FROM_GROUP({ commit, rootState }, { eventGroupOid, eventOids }) {
    commit('SET_IS_REMOVING_EVENTS_FROM_GROUP', true)
    try {
      const { $api } = getContext()
      const promoterOid = rootState.authModule.account.promoterOid

      // Fetch the current event group to get all current events
      const currentGroup = await $api.event.getEventGroup(promoterOid, eventGroupOid)

      // Convert eventOids to array if it's a single value
      const oidsToRemove = Array.isArray(eventOids) ? eventOids : [eventOids]

      // Calculate which events to keep (all current events except the ones to remove)
      const currentEventOids = currentGroup.events?.map((event: any) => event.oid) || []
      const eventOidsToKeep = currentEventOids.filter((oid: number) => !oidsToRemove.includes(oid))

      // Use PUT to replace the event list (keeps specified events, removes others)
      await $api.event.removeEventsFromGroup(promoterOid, eventGroupOid, eventOidsToKeep)

      // Optionally refresh the current event group to show updated events
      const updatedGroup = await $api.event.getEventGroup(promoterOid, eventGroupOid)
      commit('UPDATE_EVENT_GROUP', updatedGroup)

      return true
    }
    catch (error) {
      console.error('Error removing events from group:', error)
      throw error
    }
    finally {
      commit('SET_IS_REMOVING_EVENTS_FROM_GROUP', false)
    }
  },

  /**
  * Fetch upcoming events and groups for the current promoter.
  * Supports pagination with `top` and `skip` parameters, as well as search and filtering.
  * @async
  * @function FETCH_UPCOMING_ITEMS
  * @param {Object} context - Vuex action context
  * @param {Object} params - Search and pagination parameters
  * @returns {Promise<any>} API response containing upcoming items.
  */

  async FETCH_UPCOMING_ITEMS({ commit, rootState }, params: UpcomingEventsSearchParams & { reload?: boolean, append?: boolean } = {}) {
    const { reload = false, append = false, ...searchParams } = params

    commit('SET_IS_FETCHING_UPCOMING_ITEMS', true)

    try {
      const { $api } = getContext()
      const promoterOid = rootState.authModule.account.promoterOid

      const response = await $api.event.getUpcomingEvents(promoterOid, searchParams)

      if (reload || !append) {
        commit('SET_UPCOMING_ITEMS', {
          upcomingItems: response.rows,
          totalCount: response.count,
          hasNoMore: response.rows.length < (searchParams.top || 24),
        })
      }
      else {
        commit('APPEND_UPCOMING_ITEMS', {
          upcomingItems: response.rows,
          totalCount: response.count,
          hasNoMore: response.rows.length < (searchParams.top || 24),
        })
      }

      return response
    }
    catch (error) {
      console.error('Error fetching upcoming items:', error)
      throw error
    }
    finally {
      commit('SET_IS_FETCHING_UPCOMING_ITEMS', false)
    }
  },

  async FETCH_MORE_UPCOMING_ITEMS({ state, dispatch }, params: UpcomingEventsSearchParams = {}) {
    if (state.isFetchingUpcomingItems || state.hasNoMoreUpcomingItems) {
      return
    }

    const currentCount = state.upcomingItems.length
    await dispatch('FETCH_UPCOMING_ITEMS', {
      ...params,
      skip: currentCount,
      append: true,
    })
  },

  async RESET_AND_FETCH_UPCOMING_ITEMS({ commit, dispatch }, params: UpcomingEventsSearchParams = {}) {
    commit('RESET_UPCOMING_ITEMS')
    await dispatch('FETCH_UPCOMING_ITEMS', {
      ...params,
      reload: true,
    })
  },

  async DELETE_UPCOMING_EVENT_GROUP({ commit, rootState, dispatch }, eventGroupOid: number) {
    try {
      const { $api } = getContext()
      const promoterOid = rootState.authModule.account.promoterOid

      await $api.event.deleteEventGroup(promoterOid, eventGroupOid)

      // Remove from upcoming items
      commit('REMOVE_UPCOMING_ITEM', { type: 'event-group', oid: eventGroupOid })

      // Also remove from regular event groups if it exists there
      commit('DELETE_EVENT_GROUP', eventGroupOid)

      return true
    }
    catch (error) {
      console.error('Error deleting event group from upcoming:', error)
      throw error
    }
  },

  /**
  * Fetch past events and groups for the current promoter.
  * Supports pagination with `top` and `skip` parameters, as well as search and filtering.
  * @async
  * @function FETCH_PAST_ITEMS
  * @param {Object} context - Vuex action context
  * @param {Object} params - Search and pagination parameters
  * @returns {Promise<any>} API response containing past items.
  */

  async FETCH_PAST_ITEMS({ commit, rootState }, params: UpcomingEventsSearchParams & { reload?: boolean, append?: boolean } = {}) {
    const { reload = false, append = false, ...searchParams } = params

    commit('SET_IS_FETCHING_PAST_ITEMS', true)

    try {
      const { $api } = getContext()
      const promoterOid = rootState.authModule.account.promoterOid

      const response = await $api.event.getPastEvents(promoterOid, searchParams)

      if (reload || !append) {
        commit('SET_PAST_ITEMS', {
          pastItems: response.rows,
          totalCount: response.count,
          hasNoMore: response.rows.length < (searchParams.top || 24),
        })
      }
      else {
        commit('APPEND_PAST_ITEMS', {
          pastItems: response.rows,
          totalCount: response.count,
          hasNoMore: response.rows.length < (searchParams.top || 24),
        })
      }

      return response
    }
    catch (error) {
      console.error('Error fetching past items:', error)
      throw error
    }
    finally {
      commit('SET_IS_FETCHING_PAST_ITEMS', false)
    }
  },

  async FETCH_MORE_PAST_ITEMS({ state, dispatch }, params: UpcomingEventsSearchParams = {}) {
    if (state.isFetchingPastItems || state.hasNoMorePastItems) {
      return
    }

    const currentCount = state.pastItems.length
    await dispatch('FETCH_PAST_ITEMS', {
      ...params,
      skip: currentCount,
      append: true,
    })
  },

  async RESET_AND_FETCH_PAST_ITEMS({ commit, dispatch }, params: UpcomingEventsSearchParams = {}) {
    commit('RESET_PAST_ITEMS')
    await dispatch('FETCH_PAST_ITEMS', {
      ...params,
      reload: true,
    })
  },

  // Archived Events Actions (combined events and groups)

  async FETCH_ARCHIVED_ITEMS({ commit, rootState }, params: UpcomingEventsSearchParams & { reload?: boolean, append?: boolean } = {}) {
    const { reload = false, append = false, ...searchParams } = params

    if (!append) {
      commit('SET_IS_FETCHING_ARCHIVED_ITEMS', true)
    }

    try {
      const { $api } = getContext()
      const promoterOid = rootState.authModule.account.promoterOid

      const response = await $api.event.getArchivedEvents(promoterOid, searchParams)

      if (reload || !append) {
        commit('SET_ARCHIVED_ITEMS', {
          archivedItems: response.rows,
          totalCount: response.count,
          hasNoMore: response.rows.length < (searchParams.top || 24),
        })
      }
      else {
        commit('APPEND_ARCHIVED_ITEMS', {
          archivedItems: response.rows,
          totalCount: response.count,
          hasNoMore: response.rows.length < (searchParams.top || 24),
        })
      }

      return response
    }
    catch (error) {
      console.error('Error fetching archived items:', error)
      throw error
    }
    finally {
      commit('SET_IS_FETCHING_ARCHIVED_ITEMS', false)
    }
  },

  async FETCH_MORE_ARCHIVED_ITEMS({ state, dispatch }, params: UpcomingEventsSearchParams = {}) {
    if (state.isFetchingArchivedItems || state.hasNoMoreArchivedItems) {
      return
    }

    const currentCount = state.archivedItems.length
    await dispatch('FETCH_ARCHIVED_ITEMS', {
      ...params,
      skip: currentCount,
      append: true,
    })
  },

  async RESET_AND_FETCH_ARCHIVED_ITEMS({ commit, dispatch }, params: UpcomingEventsSearchParams = {}) {
    commit('RESET_ARCHIVED_ITEMS')
    await dispatch('FETCH_ARCHIVED_ITEMS', {
      ...params,
      reload: true,
    })
  },

  async ARCHIVE_EVENT({ rootState, commit }, { type, oid }: { type: 'event' | 'event-group', oid: number }) {
    try {
      const { $api, $arNotification } = getContext()
      const promoterOid = rootState.authModule.account.promoterOid

      if (type === 'event') {
        await $api.event.archiveEvent(promoterOid, oid)
      }
      else if (type === 'event-group') {
        await $api.event.archiveEventGroup(promoterOid, oid)
      }

      // Remove from upcoming/past lists
      commit('REMOVE_UPCOMING_ITEM', { type, oid })
      commit('REMOVE_PAST_ITEM', { type, oid })

      return oid
    }
    catch (error) {
      const { $arNotification } = getContext()
      console.error('Error archiving item:', error)
      $arNotification.push({
        type: 'error',
        message: type === 'event' ? 'Failed to archive event' : 'Failed to archive event group',
      })
      throw error
    }
  },

  async UNARCHIVE_EVENT({ rootState, commit }, { type, oid }: { type: 'event' | 'event-group', oid: number }) {
    try {
      const { $api, $arNotification } = getContext()
      const promoterOid = rootState.authModule.account.promoterOid

      if (type === 'event') {
        await $api.event.unarchiveEvent(promoterOid, oid)
      }
      else if (type === 'event-group') {
        await $api.event.unarchiveEventGroup(promoterOid, oid)
      }

      // Remove from archived list
      commit('REMOVE_ARCHIVED_ITEM', { type, oid })

      $arNotification.push({
        type: 'success',
        message: type === 'event' ? 'Event unarchived successfully' : 'Event group unarchived successfully',
      })

      return oid
    }
    catch (error) {
      const { $arNotification } = getContext()
      console.error('Error unarchiving item:', error)
      $arNotification.push({
        type: 'error',
        message: type === 'event' ? 'Failed to unarchive event' : 'Failed to unarchive event group',
      })
      throw error
    }
  },

  // Event Orders Actions
  async FETCH_EVENT_ORDERS(
    { commit, rootState },
    {
      eventOid = null,
      groupOid = null,
      selectedEventIds = [],
      searchString = '',
      sortBy = { key: 'orderDate', order: 'desc' },
      top = 50,
      skip = 0,
      reload = false,
    },
  ) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account

    try {
      commit('SET_IS_FETCHING_EVENT_ORDERS', true)

      // Determine if we're fetching for a group or single event
      // Use group API whenever groupOid is provided, regardless of selectedEventIds
      const isGroupMode = !!groupOid

      let response

      // groupOid=0 is a virtual sentinel that tells the backend to skip the
      // event-group lookup and use the provided event-oids directly. This is
      // needed because the single-event endpoint cannot sort by orderCost (JSONB).
      const effectiveGroupOid = groupOid || 0
      const useGroupEndpoint = isGroupMode || (sortBy.key === 'value' || sortBy.key === 'orderTotal')

      if (useGroupEndpoint) {
        // Use the group orders API for multiple events
        // Construct orderby parameter for group API
        let orderByParam = 'orderDate DESC' // Default sort for group API
        if (sortBy.key === 'orderDate' || sortBy.key === 'sysMtime') {
          orderByParam = `orderDate ${sortBy.order.toUpperCase()}`
        }
        else if (sortBy.key === 'totalTickets' || sortBy.key === 'tickets') {
          orderByParam = `totalTickets ${sortBy.order.toUpperCase()}`
        }
        else if (sortBy.key === 'value' || sortBy.key === 'orderTotal') {
          orderByParam = `orderCost ${sortBy.order.toUpperCase()}`
        }

        const payload: Record<string, any> = {
          $search: searchString && searchString.trim() ? searchString.trim() : undefined,
          $top: top,
          $skip: skip,
          $orderby: orderByParam,
          $select: 'oid,fanPromoterAccount,totalTickets,status,orderDate,orderCosts,provider',
          $count: true,
        }

        // Always include event-oids when provided (including empty array)
        // - undefined/not provided = select all events (backend default)
        // - [] = select no events (explicit empty selection)
        // - [1,2,3] = select specific events
        if (selectedEventIds !== undefined && selectedEventIds.length > 0) {
          payload['event-oids'] = selectedEventIds
        }
        else if (!isGroupMode && eventOid) {
          // If sorting orderTotal for a single event via group endpoint
          payload['event-oids'] = [eventOid]
        }

        const groupResponse = await requestApi('fetchEventGroupOrders', {
          promoterOid: promoterOid,
          params: {
            groupOid: effectiveGroupOid,
          },
          body: payload,
        })
        // Backend now returns standard format {rows, count}
        response = {
          orders: groupResponse.rows || [],
          totalCount: groupResponse.count || 0,
        }
      }
      else {
        // Use the single event orders API
        // Build filter criteria based on the actual API pattern
        let filterCriteria = `totalTickets>0 AND eventOid=${eventOid}`

        // Add search terms if provided
        if (searchString && searchString.trim()) {
          const searchTerm = searchString.trim()
          // Search in customer name fields and order ID
          filterCriteria += ` AND (firstName ILIKE "%${searchTerm}%" OR orderId ILIKE "%${searchTerm}%" OR fanPromoterAccount[firstName] ILIKE "%${searchTerm}%" OR fanPromoterAccount[lastName] ILIKE "%${searchTerm}%" OR fanPromoterAccount[emailAddress] ILIKE "%${searchTerm}%")`
        }

        // Construct orderby parameter safely - using actual API field names
        let orderByParam = 'orderDate desc, oid desc' // Default sort
        if (sortBy.key === 'orderDate' || sortBy.key === 'sysMtime') {
          orderByParam = `orderDate ${sortBy.order}, oid ${sortBy.order}`
        }
        else if (sortBy.key === 'totalTickets' || sortBy.key === 'tickets') {
          orderByParam = `totalTickets ${sortBy.order}, oid ${sortBy.order}`
        }
        else if (sortBy.key === 'value' || sortBy.key === 'orderTotal') {
          // orderCost sorting not natively supported on single-event endpoint
          // Falls back to descending orderDate (fallback if groupOid missing)
          orderByParam = `orderDate ${sortBy.order}, oid ${sortBy.order}`
        }

        const singleEventResponse = await requestApi('fetchEventOrders', {
          promoterOid: promoterOid,
          eventOid: eventOid,
          params: {
            $filter: filterCriteria,
            $top: top,
            $skip: skip,
            $orderby: orderByParam,
            $select: 'fanPromoterAccount,orderCosts,firstName,totalTickets,value,orderId,provider,status,orderDate',
            $count: false, // Never make it true due to performance issue
          },
        })
        // Wrap single event response in same format as group response
        // $count is disabled for performance — infer whether more pages exist by
        // checking if a full page was returned
        const orders = singleEventResponse || []
        response = {
          orders,
          totalCount: orders.length === top ? skip + top + 1 : skip + orders.length,
        }
      }

      // Store the response
      if (reload) {
        commit('SET_EVENT_ORDERS', response.orders || [])
        commit('SET_EVENT_ORDERS_TOTAL_COUNT', response.totalCount || 0)
      }
      else {
        commit('APPEND_EVENT_ORDERS', response.orders || [])
      }

      return true
    }
    catch (error) {
      console.error('Error fetching event orders:', error)
      return false
    }
    finally {
      commit('SET_IS_FETCHING_EVENT_ORDERS', false)
    }
  },

  async BATCH_ARCHIVE_ITEMS(
    { rootState, commit },
    items: Array<{ type: 'event' | 'event-group', oid: number }>,
  ): Promise<BatchArchiveResult> {
    const { $api } = getContext()
    const promoterOid = rootState.authModule.account!.promoterOid

    const eventOids = items.filter(i => i.type === 'event').map(i => i.oid)
    const groupOids = items.filter(i => i.type === 'event-group').map(i => i.oid)

    const result: BatchArchiveResult = {
      archivedCount: 0,
      alreadyArchivedCount: 0,
      notFoundCount: 0,
      eventOids: [],
      eventGroupOids: [],
      refreshMode: undefined,
      errors: [],
    }

    const eventBatches = chunk(eventOids, BATCH_SIZE)
    const groupBatches = chunk(groupOids, BATCH_SIZE)

    type PromiseResult = {
      type: 'event' | 'group'
      res?: BatchArchiveEventsResponse | BatchArchiveEventGroupsResponse
      error?: unknown
    }

    const tasks: (() => Promise<PromiseResult>)[] = []

    for (const batch of eventBatches) {
      tasks.push(() =>
        $api.event.batchArchiveEvents(promoterOid, batch)
          .then(res => ({ type: 'event' as const, res }))
          .catch(error => ({ type: 'event' as const, error })),
      )
    }

    for (const batch of groupBatches) {
      tasks.push(() =>
        $api.event.batchArchiveEventGroups(promoterOid, batch)
          .then(res => ({ type: 'group' as const, res }))
          .catch(error => ({ type: 'group' as const, error })),
      )
    }

    const results = await promisePool(tasks, MAX_CONCURRENT_BATCHES)

    for (const { type, res, error } of results) {
      if (error) {
        const errorMessage = (error as CustomApiError)?.message
          || (type === 'event' ? 'Failed to archive events' : 'Failed to archive event groups')
        result.errors.push(errorMessage)
        continue
      }

      result.archivedCount += res!.archivedCount ?? 0
      result.alreadyArchivedCount += res!.alreadyArchivedCount ?? 0
      result.notFoundCount += res!.notFoundCount ?? 0

      if (type === 'event') {
        const eventRes = res as BatchArchiveEventsResponse
        result.eventOids.push(...eventRes.eventOids)
      }
      else {
        const groupRes = res as BatchArchiveEventGroupsResponse
        result.eventGroupOids.push(...groupRes.eventGroupOids)
        if (groupRes.refreshMode === 'async') {
          result.refreshMode = 'async'
        }
        else if (!result.refreshMode && groupRes.refreshMode) {
          result.refreshMode = groupRes.refreshMode
        }
      }
    }

    if (result.eventOids.length > 0) {
      commit('REMOVE_ITEMS_BATCH', { type: 'event', oids: result.eventOids })
    }
    if (result.eventGroupOids.length > 0) {
      commit('REMOVE_ITEMS_BATCH', { type: 'event-group', oids: result.eventGroupOids })
    }

    return result
  },

  async BATCH_UNARCHIVE_ITEMS(
    { rootState, commit },
    items: Array<{ type: 'event' | 'event-group', oid: number }>,
  ): Promise<BatchUnarchiveResult> {
    const { $api } = getContext()
    const promoterOid = rootState.authModule.account!.promoterOid

    const eventOids = items.filter(i => i.type === 'event').map(i => i.oid)
    const groupOids = items.filter(i => i.type === 'event-group').map(i => i.oid)

    const result: BatchUnarchiveResult = {
      unarchivedCount: 0,
      alreadyUnarchivedCount: 0,
      notFoundCount: 0,
      eventOids: [],
      eventGroupOids: [],
      refreshMode: undefined,
      errors: [],
    }

    const eventBatches = chunk(eventOids, BATCH_SIZE)
    const groupBatches = chunk(groupOids, BATCH_SIZE)

    type PromiseResult = {
      type: 'event' | 'group'
      res?: BatchUnarchiveEventsResponse | BatchUnarchiveEventGroupsResponse
      error?: unknown
    }

    const tasks: (() => Promise<PromiseResult>)[] = []

    for (const batch of eventBatches) {
      tasks.push(() =>
        $api.event.batchUnarchiveEvents(promoterOid, batch)
          .then(res => ({ type: 'event' as const, res }))
          .catch(error => ({ type: 'event' as const, error })),
      )
    }

    for (const batch of groupBatches) {
      tasks.push(() =>
        $api.event.batchUnarchiveEventGroups(promoterOid, batch)
          .then(res => ({ type: 'group' as const, res }))
          .catch(error => ({ type: 'group' as const, error })),
      )
    }

    const results = await promisePool(tasks, MAX_CONCURRENT_BATCHES)

    for (const { type, res, error } of results) {
      if (error) {
        const errorMessage = (error as CustomApiError)?.message
          || (type === 'event' ? 'Failed to unarchive events' : 'Failed to unarchive event groups')
        result.errors.push(errorMessage)
        continue
      }

      result.unarchivedCount += res!.unarchivedCount ?? 0
      result.alreadyUnarchivedCount += res!.alreadyUnarchivedCount ?? 0
      result.notFoundCount += res!.notFoundCount ?? 0

      if (type === 'event') {
        const eventRes = res as BatchUnarchiveEventsResponse
        result.eventOids.push(...eventRes.eventOids)
      }
      else {
        const groupRes = res as BatchUnarchiveEventGroupsResponse
        result.eventGroupOids.push(...groupRes.eventGroupOids)
        if (groupRes.refreshMode === 'async') {
          result.refreshMode = 'async'
        }
        else if (!result.refreshMode && groupRes.refreshMode) {
          result.refreshMode = groupRes.refreshMode
        }
      }
    }

    if (result.eventOids.length > 0) {
      commit('REMOVE_ARCHIVED_ITEMS_BATCH', { type: 'event', oids: result.eventOids })
    }
    if (result.eventGroupOids.length > 0) {
      commit('REMOVE_ARCHIVED_ITEMS_BATCH', { type: 'event-group', oids: result.eventGroupOids })
    }

    return result
  },
}
